import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_INTERESTS_LOAD,
    ACTION_INTERESTS_LOAD_BY_ID,
    ACTION_INTERESTS_LOAD_FAILURE,
    ACTION_INTERESTS_LOAD_SUCCESS,

    ACTION_INTERESTS_SAVE,
    ACTION_INTERESTS_SAVE_SUCCESS,
    ACTION_INTERESTS_SAVE_FAILURE,

    ACTION_INTERESTS_CREATE,
    ACTION_INTERESTS_CREATE_SUCCESS,
    ACTION_INTERESTS_CREATE_FAILURE,

    ACTION_INTERESTS_VALIDATE,
    ACTION_INTERESTS_VALIDATE_SUCCESS,
    ACTION_INTERESTS_VALIDATE_FAILURE,

    ACTION_INTERESTS_CLEAR,
    ACTION_INTERESTS_FILTER,

    InterestAction
} from '../actions/interests';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IInterestState {
    formData: Dtos.Interest | Dtos.Interest[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Interest[];
}

const initialState: IInterestState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: IInterestState, _isLoading: boolean): IInterestState {
    const newState: IInterestState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IInterestState,
    formData: Dtos.Interest | Dtos.Interest[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IInterestState {
    const newState: IInterestState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IInterestState, formFilter: IFormFilter): IInterestState {

    const interests: Dtos.Interest[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = interests ? interests.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IInterestState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: IInterestState, responseStatus: Dtos.ResponseStatus): IInterestState {
    const newState: IInterestState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IInterestState, propertiesToClear?: string[]): IInterestState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IInterestState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IInterestState, _isSaving: boolean): IInterestState {
    const newState: IInterestState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IInterestState,
    formData: Dtos.Interest | Dtos.Interest[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInterestState {
    const newState: IInterestState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IInterestState, responseStatus: Dtos.ResponseStatus): IInterestState {
    const newState: IInterestState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IInterestState, _isCreating: boolean): IInterestState {
    const newState: IInterestState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IInterestState,
    formData: Dtos.Interest,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IInterestState {
    const newState: IInterestState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IInterestState, responseStatus: Dtos.ResponseStatus): IInterestState {
    const newState: IInterestState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IInterestState, _isValidating: boolean): IInterestState {
    const newState: IInterestState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IInterestState,
    formData: Dtos.Interest,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInterestState {
    const newState: IInterestState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IInterestState, responseStatus: Dtos.ResponseStatus): IInterestState {
    const newState: IInterestState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}


const InterestReducer: Reducer<IInterestState> = (state: IInterestState = initialState, action: InterestAction) => {
    switch (action.type) {
        case ACTION_INTERESTS_LOAD:
        case ACTION_INTERESTS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_INTERESTS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INTERESTS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_INTERESTS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_INTERESTS_SAVE:
            return saving(state, true);
        case ACTION_INTERESTS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INTERESTS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_INTERESTS_CREATE:
            return creating(state, true);
        case ACTION_INTERESTS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_INTERESTS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_INTERESTS_VALIDATE:
            return validating(state, true);
        case ACTION_INTERESTS_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INTERESTS_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_INTERESTS_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default InterestReducer;